import { useEffect, useState } from "react";
import {
  Box,
  Modal,
  IconButton,
  CircularProgress,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Google Docs viewer URL — readable embed without Chrome’s left thumbnail strip.
 */
export function getGooglePdfViewerUrl(pdfUrl) {
  if (!pdfUrl) return "";
  return `https://docs.google.com/viewer?url=${encodeURIComponent(pdfUrl)}&embedded=true`;
}

const defaultBoxSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  width: "92%",
  maxWidth: 960,
  height: "88vh",
  borderRadius: 2,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  outline: "none",
};

/**
 * Reusable modal to show a PDF (upload document instruction or any PDF URL).
 *
 * @param {string} pdfUrl - Direct PDF URL (e.g. Firebase Storage with token)
 * @param {boolean} open
 * @param {function} onClose
 * @param {string} [title] - iframe title / a11y
 * @param {string} [newTabLabel] - link text (default "Open in new tab")
 * @param {boolean} [useGoogleViewer=true] - embed via Google Viewer for readability
 * @param {object} [sx] - merged into outer modal content Box
 */
export default function DocumentInstructionModal({
  pdfUrl,
  open,
  onClose,
  title = "Document",
  newTabLabel = "Open in new tab",
  useGoogleViewer = true,
  sx = {},
}) {
  const [pdfLoading, setPdfLoading] = useState(true);

  const iframeSrc = open
    ? useGoogleViewer
      ? getGooglePdfViewerUrl(pdfUrl)
      : pdfUrl
    : undefined;

  // Prefetch PDF when URL is known (helps when opening in new tab and sometimes iframe).
  useEffect(() => {
    if (!pdfUrl) return undefined;
    const link = document.createElement("link");
    link.rel = "prefetch";
    link.href = pdfUrl;
    link.as = "document";
    document.head.appendChild(link);
    return () => {
      if (link.parentNode) link.parentNode.removeChild(link);
    };
  }, [pdfUrl]);

  // Reset loading when opening; timeout fallback if onLoad never fires.
  useEffect(() => {
    if (open) setPdfLoading(true);
  }, [open, pdfUrl]);

  useEffect(() => {
    if (!open || !pdfLoading) return undefined;
    const t = setTimeout(() => setPdfLoading(false), 12000);
    return () => clearTimeout(t);
  }, [open, pdfLoading]);

  if (!pdfUrl) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ ...defaultBoxSx, ...sx }}>
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.5,
            py: 1,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Link
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ fontSize: 14 }}
          >
            {newTabLabel}
          </Link>
          <IconButton
            onClick={onClose}
            sx={{
              color: "#fff",
              bgcolor: "red",
              "&:hover": { bgcolor: "darkred" },
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ position: "relative", flex: 1, minHeight: 0, bgcolor: "#eee" }}>
          {pdfLoading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(255,255,255,0.9)",
                zIndex: 10,
              }}
            >
              <CircularProgress sx={{ color: "green" }} size={60} />
            </Box>
          )}

          <iframe
            key={open ? pdfUrl : "closed"}
            src={iframeSrc}
            title={title}
            width="100%"
            height="100%"
            style={{ border: "none", display: "block" }}
            onLoad={() => setPdfLoading(false)}
          />
        </Box>
      </Box>
    </Modal>
  );
}
